import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Auth
import { AuthProvider } from './components/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Public / lead capture
import LandingPage from './components/LandingPage';
import LeadCaptureForm from './components/LeadCaptureForm';
import TierSelection from './components/TierSelection';
import ThankYou from './components/ThankYou';

// Auth pages
import Login from './components/Login';

// CISO Advisor app
import CustomerDashboard from './components/CustomerDashboard';
import CapabilityAssessment from './components/CapabilityAssessment';
import GapAnalysis from './components/GapAnalysis';
import RiskAnalysis from './components/RiskAnalysis';
import StrategyPaper from './components/StrategyPaper';
import RemediationPunchList from './components/RemediationPunchList';
import ExecutionDashboard from './components/ExecutionDashboard';
import EvidenceCollection from './components/EvidenceCollection';
import CredentialManagement from './components/CredentialManagement';
import ReportingDashboard from './components/ReportingDashboard';
import RegulatoryReports from './components/RegulatoryReports';
import OnboardingWizard from './components/OnboardingWizard';
import FileUpload from './components/FileUpload';
import FileList from './components/FileList';
import FeedbackForm from './components/FeedbackForm';

// Admin
import Dashboard from './components/Dashboard';
import AdminLeadDashboard from './components/AdminLeadDashboard';
import AdminCustomers from './components/AdminCustomers';
import UserManagement from './components/UserManagement';

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>

            {/* ── Public Landing Page ───────────────────────────────── */}
            <Route path="/"               element={<LandingPage />} />

            {/* ── Public Routes ─────────────────────────────────────── */}
            <Route path="/lead-capture"   element={<LeadCaptureForm />} />
            <Route path="/tier-selection" element={<TierSelection />} />
            <Route path="/thank-you"      element={<ThankYou />} />
            <Route path="/login"          element={<Login />} />

            {/* ── CISO Advisor — any authenticated user ─────────────── */}
            <Route path="/ciso-dashboard" element={
              <ProtectedRoute><CustomerDashboard /></ProtectedRoute>
            } />
            <Route path="/onboarding" element={
              <ProtectedRoute><OnboardingWizard /></ProtectedRoute>
            } />
            <Route path="/upload" element={
              <ProtectedRoute><FileUpload apiBaseUrl="http://localhost:8000/api" /></ProtectedRoute>
            } />
            <Route path="/files" element={
              <ProtectedRoute><FileList apiBaseUrl="http://localhost:8000/api" /></ProtectedRoute>
            } />

            {/* ── Assessments ───────────────────────────────────────── */}
            <Route path="/assessment" element={
              <ProtectedRoute permission="read_assessments">
                <CapabilityAssessment />
              </ProtectedRoute>
            } />
            <Route path="/gap-analysis" element={
              <ProtectedRoute permission="read_assessments">
                <GapAnalysis />
              </ProtectedRoute>
            } />

            {/* ── Risk & Strategy ───────────────────────────────────── */}
            <Route path="/risk-analysis" element={
              <ProtectedRoute permission="read_assessments">
                <RiskAnalysis />
              </ProtectedRoute>
            } />
            <Route path="/strategy" element={
              <ProtectedRoute permission="read_strategy">
                <StrategyPaper />
              </ProtectedRoute>
            } />
            <Route path="/remediation" element={
              <ProtectedRoute permission="read_strategy">
                <RemediationPunchList />
              </ProtectedRoute>
            } />
            <Route path="/execution" element={
              <ProtectedRoute permission="read_strategy">
                <ExecutionDashboard />
              </ProtectedRoute>
            } />
            <Route path="/evidence" element={
              <ProtectedRoute permission="read_strategy">
                <EvidenceCollection />
              </ProtectedRoute>
            } />

            {/* ── Reports ───────────────────────────────────────────── */}
            <Route path="/reports" element={
              <ProtectedRoute permission="read_reports">
                <ReportingDashboard />
              </ProtectedRoute>
            } />
            <Route path="/reports/regulatory" element={
              <ProtectedRoute permission="read_reports">
                <RegulatoryReports />
              </ProtectedRoute>
            } />

            {/* ── Settings — admin only ─────────────────────────────── */}
            <Route path="/settings/integrations" element={
              <ProtectedRoute permission="manage_credentials">
                <CredentialManagement />
              </ProtectedRoute>
            } />

            {/* ── Admin Panel ───────────────────────────────────────── */}
            <Route path="/admin/leads" element={
              <ProtectedRoute permission="view_admin_panel">
                <AdminLeadDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/customers" element={
              <ProtectedRoute permission="view_admin_panel">
                <AdminCustomers />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute permission="manage_users">
                <UserManagement />
              </ProtectedRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute permission="view_admin_panel">
                <Dashboard />
              </ProtectedRoute>
            } />

            <Route path="/feedback" element={
              <ProtectedRoute><FeedbackForm /></ProtectedRoute>
            } />

            {/* ── Catch-all: unknown routes go to landing page ──────── */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
